/**
 * useTripAccess Hook - Validates user access to a trip before rendering
 * Single Responsibility: Only handles trip access validation
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const ACCESS_STATUS = {
  LOADING: 'loading',
  AUTHORIZED: 'authorized',
  PENDING: 'pending',
  REJECTED: 'rejected',
  NOT_MEMBER: 'not_member',
  NOT_FOUND: 'not_found',
  ERROR: 'error',
};

/**
 * Hook to check if current user has access to a specific trip
 * @param {string} tripId - The trip ID to check access for
 * @returns {Object} - { status, tripData, memberData, isOrganizer, refetch }
 */
export function useTripAccess(tripId) {
  const { api, user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState(ACCESS_STATUS.LOADING);
  const [tripData, setTripData] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [error, setError] = useState(null);

  const checkAccess = useCallback(async () => {
    if (!tripId || !isAuthenticated || !user) {
      setStatus(ACCESS_STATUS.ERROR);
      return;
    }

    setStatus(ACCESS_STATUS.LOADING);
    setError(null);

    try {
      // Single API call to check membership status
      const response = await api.get(`/trips/${tripId}/members/me`);
      const data = response.data;

      // Store trip data
      setTripData({ name: data.trip_name });
      setIsOrganizer(data.is_organizer);

      // Store member data if exists
      if (data.member) {
        setMemberData(data.member);
      }

      // Map backend status to access status
      switch (data.status) {
        case 'MEMBER':
          setStatus(ACCESS_STATUS.AUTHORIZED);
          break;
        case 'PENDING':
          setStatus(ACCESS_STATUS.PENDING);
          break;
        case 'REJECTED':
          setStatus(ACCESS_STATUS.REJECTED);
          break;
        case 'NOT_MEMBER':
          // Organizer always has access even without membership record
          if (data.is_organizer) {
            setStatus(ACCESS_STATUS.AUTHORIZED);
          } else {
            setStatus(ACCESS_STATUS.NOT_MEMBER);
          }
          break;
        default:
          setStatus(ACCESS_STATUS.NOT_MEMBER);
      }

    } catch (err) {
      console.error('Error checking trip access:', err);
      
      // Handle specific errors
      if (err.response?.status === 404) {
        setStatus(ACCESS_STATUS.NOT_FOUND);
        return;
      }
      
      setError(err.message || 'Erro ao verificar acesso');
      setStatus(ACCESS_STATUS.ERROR);
    }
  }, [tripId, api, user, isAuthenticated]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return {
    status,
    tripData,
    memberData,
    isOrganizer,
    error,
    refetch: checkAccess,
    isLoading: status === ACCESS_STATUS.LOADING,
    isAuthorized: status === ACCESS_STATUS.AUTHORIZED,
    isPending: status === ACCESS_STATUS.PENDING,
    isRejected: status === ACCESS_STATUS.REJECTED,
  };
}

export default useTripAccess;
